// Port of ios/Sources/ViewModels/ThreadsDerivation.swift.
import type { ThreadRecord, UserRecord } from './records';
import { messagePreview, userDisplayName } from './channelListDerivation';

/**
 * Which tab of the Threads screen is showing (shared/API.md §13.8). 'unreads' and 'all' merge
 * followed threads (the server's) with the ones the reader does not follow (derived on the device);
 * 'following' / 'notFollowing' show one kind each. Report 2026-10-27: a channel row said «the
 * newest message is a reply» about a thread this screen never listed.
 */
export type ThreadsTab = 'unreads' | 'all' | 'following' | 'notFollowing';

/** The three «Mark as read» choices (§13.8). */
export type ThreadsMarkScope = 'all' | 'following' | 'notFollowing';

/**
 * The Threads entry pinned at the top of the channel list. Both numbers come from the
 * SERVER's totals, never from summing the cached page — a badge that disagrees with the
 * desktop client is worse than no badge.
 */
export interface ThreadsEntry {
  unreadThreads: number;
  unreadMentions: number;
}

/** One row of the Threads screen, fully resolved — the view never sees a `ThreadRecord`. */
export interface DisplayThread {
  id: string;
  channelId: string;
  channelName: string;
  /** "Ana Torres", "Ana Torres y Luis Mendoza", "Ana, Luis y 3 más" — already composed. */
  participantSummary: string;
  /**
   * The thread's NEWEST reply, flattened and prefixed — falling back to the root only when
   * no reply is cached. A row saying "2 new" above the message already read answers the
   * wrong question: this line is for showing what has NOT been seen.
   */
  preview: string;
  lastReplyAt: number;
  replyCount: number;
  unreadReplies: number;
  unreadMentions: number;
  isUrgent: boolean;
  avatarUserIds: string[];
  avatarVersions: Record<string, number>;
  /** False for a thread derived on the device (§13.8): its unread is device-only. */
  isFollowing: boolean;
}

function entryHasUnread(entry: ThreadsEntry): boolean {
  return entry.unreadThreads > 0 || entry.unreadMentions > 0;
}

function threadIsUnread(thread: Pick<DisplayThread, 'unreadReplies' | 'unreadMentions'>): boolean {
  return thread.unreadReplies > 0 || thread.unreadMentions > 0;
}

/**
 * Whether the global Threads view exists for this user at all. Mattermost gates collapsed
 * reply threads three ways and all three must agree: the server capability (what the threads
 * endpoint itself last answered — trusted over config, because it is the only source actually
 * TESTED; 501 = off, and nothing overrules it), the `CollapsedThreads` config, and — when the
 * config leaves it to the user — their preference. The failure mode avoided: a Threads row
 * that opens onto a screen which can only ever say "no threads". Hiding the row is the honest
 * degradation.
 */
function isEnabled(
  serverCapability: boolean | null,
  collapsedThreadsConfig: string | null,
  userPreference: string | null,
): boolean {
  if (serverCapability === false) return false;
  switch (collapsedThreadsConfig) {
    case 'disabled':
      return false;
    case 'always_on':
      return true;
    case 'default_on':
      return userPreference !== 'off';
    case 'default_off':
      return userPreference === 'on';
    // No config read yet (or a future server's unrecognised value): fall back to whether
    // the endpoint has actually worked. Before the first sync that is null, and the row
    // stays hidden until we know.
    default:
      return serverCapability === true;
  }
}

export const ThreadsAvailability = {
  isEnabled,
} as const;

// Pure logic for the global Threads view (shared/API.md §13) — the badge arithmetic, the tab
// filter and the mark-all-read transition, testable against plain values. A decision-for-
// decision port of Android's `ThreadsDerivation`.

function entry(unreadThreads: number, unreadMentions: number): ThreadsEntry {
  return { unreadThreads: Math.max(0, unreadThreads), unreadMentions: Math.max(0, unreadMentions) };
}

/**
 * What the Hilos row's badge SAYS — the number of threads waiting, which is exactly the
 * number of rows the "No leídos" tab will list.
 *
 * It used to say the MENTION count, which is the convention for a channel row and is wrong
 * here. A channel badge counts messages inside one conversation; this row leads to a LIST,
 * and a list's badge is read as "how many are in there". Reported 2026-10-05 with a
 * screenshot: badge 1, two unread threads behind it, "this doesn't add up". Both numbers
 * were correct — 2 unread threads, 1 of them mentioning you — and nothing on screen said the
 * badge was answering the other question. Mentions keep the badge's MENTION COLOUR (and each
 * row inside still carries its own mention count), so nothing is lost.
 *
 * Falls back to the mention count if the server ever reports mentions with no unread thread:
 * the two totals arrive as separate fields, and saying "1" is better than showing nothing
 * while a mention sits unread.
 */
function entryBadge(e: ThreadsEntry): string | null {
  if (e.unreadThreads > 0) return `${e.unreadThreads}`;
  if (e.unreadMentions > 0) return `${e.unreadMentions}`;
  return null;
}

/**
 * "All your threads" shows everything followed; "Unreads" only what has news — which is
 * why mark-all-read belongs on the SECOND tab: it is the only one the action visibly
 * empties.
 */
function visibleThreads(threads: readonly DisplayThread[], tab: ThreadsTab): DisplayThread[] {
  switch (tab) {
    case 'all':
      return [...threads];
    case 'unreads':
      return threads.filter(threadIsUnread);
    case 'following':
      return threads.filter((t) => t.isFollowing);
    case 'notFollowing':
      return threads.filter((t) => !t.isFollowing);
  }
}

/** Followed (server) and not-followed (device) threads as ONE list, newest reply first; a thread
 *  present in both is the server's. Stable for equal times. */
function mergeRecords<T extends { id: string; lastReplyAt: number }>(followed: readonly T[], unfollowed: readonly T[]): T[] {
  const ids = new Set(followed.map((t) => t.id));
  return [...followed, ...unfollowed.filter((t) => !ids.has(t.id))]
    .map((t, i) => [t, i] as const)
    .sort((a, b) => b[0].lastReplyAt - a[0].lastReplyAt || a[1] - b[1])
    .map(([t]) => t);
}

/** «All threads» whenever anything is unread; the per-group choices only when BOTH groups have
 *  unread (otherwise one is a second name for «All threads», the other does nothing). */
function markScopes(threads: readonly DisplayThread[]): ThreadsMarkScope[] {
  const followed = threads.some((t) => t.isFollowing && threadIsUnread(t));
  const other = threads.some((t) => !t.isFollowing && threadIsUnread(t));
  const scopes: ThreadsMarkScope[] = [];
  if (followed || other) scopes.push('all');
  if (followed && other) scopes.push('following', 'notFollowing');
  return scopes;
}

/** Unread for a thread the reader does not follow (§13.8): cached replies by someone else newer
 *  than the baseline, or 1 when the server says a newer reply exists that is not cached. */
function unfollowedUnread(lastReplyAt: number, baseline: number, unreadCached: number, newestCachedReplyAt: number | null): number {
  if (unreadCached > 0) return unreadCached;
  if (lastReplyAt > baseline && (newestCachedReplyAt == null || newestCachedReplyAt < lastReplyAt)) return 1;
  return 0;
}

/**
 * Mark-all-read as a pure transition over the rows currently held — the screen has to
 * empty the Unreads tab ON THE TAP, not a round trip later. Same rows, same order: the
 * FOLLOWING tab must not reshuffle just because badges cleared.
 */
function markAllRead(threads: readonly DisplayThread[]): DisplayThread[] {
  return threads.map((t) => ({ ...t, unreadReplies: 0, unreadMentions: 0 }));
}

/** The entry half of Swift's overloaded `markAllRead(_: ThreadsEntry)`. */
function markAllReadEntry(_entry: ThreadsEntry): ThreadsEntry {
  return { unreadThreads: 0, unreadMentions: 0 };
}

/**
 * Marking ONE thread read: the totals come down by exactly what that row carried — never
 * below zero, even when the totals were stale.
 */
function markRead(e: ThreadsEntry, thread: DisplayThread): ThreadsEntry {
  return {
    unreadThreads: Math.max(0, e.unreadThreads - (threadIsUnread(thread) ? 1 : 0)),
    unreadMentions: Math.max(0, e.unreadMentions - thread.unreadMentions),
  };
}

/**
 * Who is in the thread. The current user is dropped — every thread you follow has you in
 * it, so your own name on every row says nothing. Unresolved names are skipped rather than
 * shown as ids; if nothing resolves, the fallback keeps the line from being blank.
 */
function participantSummary(args: {
  participantIds: readonly string[];
  currentUserId: string;
  usersById: ReadonlyMap<string, UserRecord>;
  unknownFallback: string;
  maxNames?: number;
  andLabel: string;
  moreLabel: (count: number) => string;
}): string {
  const maxNames = args.maxNames ?? 2;
  const names: string[] = [];
  for (const id of args.participantIds) {
    if (id === args.currentUserId) continue;
    const user = args.usersById.get(id);
    if (!user) continue;
    const name = userDisplayName(user);
    if (!names.includes(name)) names.push(name);
  }
  if (names.length === 0) return args.unknownFallback;
  if (names.length === 1) return names[0];
  if (names.length <= maxNames) {
    return names.slice(0, -1).join(', ') + ' ' + args.andLabel + ' ' + names[names.length - 1];
  }
  const shown = names.slice(0, maxNames).join(', ');
  return shown + ' ' + args.andLabel + ' ' + args.moreLabel(names.length - maxNames);
}

/**
 * One line for the row's message: identical treatment to the channel list's preview, for
 * the same reasons.
 */
function rootPreview(args: {
  message: string;
  authorName: string | null;
  isOwnMessage: boolean;
  youLabel: string;
  attachmentPlaceholder: string;
}): string {
  return messagePreview({
    message: args.message,
    authorName: args.authorName,
    isOwnMessage: args.isOwnMessage,
    showsAuthorPrefix: true,
    youLabel: args.youLabel,
    attachmentPlaceholder: args.attachmentPlaceholder,
  });
}

/**
 * The stacked avatars, capped. The root author leads — the thread is their message —
 * then the participants in the server's (join) order.
 */
function avatarIds(thread: Pick<ThreadRecord, 'rootUserId'>, participantIds: readonly string[], maxCount = 3): string[] {
  const ordered: string[] = [];
  for (const id of [thread.rootUserId, ...participantIds]) {
    if (id !== '' && !ordered.includes(id)) ordered.push(id);
  }
  return ordered.slice(0, maxCount);
}

export const ThreadsDerivation = {
  entry,
  entryHasUnread,
  threadIsUnread,
  entryBadge,
  visibleThreads,
  mergeRecords,
  markScopes,
  unfollowedUnread,
  markAllRead,
  markAllReadEntry,
  markRead,
  participantSummary,
  rootPreview,
  avatarIds,
} as const;
